import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, money, formatDate, downloadPdf } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useLeviWorkspaceHint } from "@/lib/leviWorkspace";
import { makeTranslator } from "@/lib/onboarding_i18n";
import { Mark } from "@/components/Logo";
import { ArrowLeft, Printer, CheckCircle2, Plus, Mail, Download } from "lucide-react";
import { newRequestKey, localizeSendError } from "@/lib/emailSend";

// Slice 2D4 Batch 3 · Localize enum values used in list-view display. DB
// stored values remain unchanged; only the label is translated.
const localizeInvoiceType = (t, type) => {
  if (!type) return type;
  const key = `invoice_types.${type}`;
  const val = t(key);
  return val === key ? type : val;
};
const localizePaymentStatus = (t, status) => {
  if (!status) return status;
  const key = `invoice_doc.payment_status_labels.${status}`;
  const val = t(key);
  return val === key ? status : val;
};

export const InvoicesList = () => {
  const { user } = useAuth();
  const { lang, t } = useLang();
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  // Slice 2D3 · The new-invoice default language reads from the
  // contractor's customer-document language preference (NOT the app
  // language). If the authenticated user hasn't hydrated yet, fall
  // back to "en" — hydration below reconciles once, and only when the
  // contractor has not deliberately picked a language in the form
  // (`languageTouched === false`).
  const [form, setForm] = useState({
    project_id: "", invoice_type: "deposit", amount: 0, due_date: "", notes: "",
    language: user?.customer_document_language || "en",
  });
  const [languageTouched, setLanguageTouched] = useState(false);

  useEffect(() => {
    api.get("/invoices").then((r) => setItems(r.data));
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);

  // Slice 2D3 · One-shot seeding. Reconciles form.language from the
  // authenticated user only when the contractor has NOT deliberately
  // picked a language in the currently open form. Never triggered by
  // app-language toggles (`lang` is not a dependency).
  useEffect(() => {
    if (!user) return;
    if (languageTouched) return;
    const pref = user.customer_document_language || "en";
    setForm((f) => (f.language === pref ? f : { ...f, language: pref }));
  }, [user, languageTouched]);

  const create = async () => {
    if (!form.project_id) { toast.error(t("invoice_form.toast_choose_project")); return; }
    try {
      const { data } = await api.post("/invoices", { ...form, amount: parseFloat(form.amount || 0) });
      setItems([data, ...items]);
      setOpen(false);
      toast.success(t("invoice_form.toast_created"));
    } catch { toast.error(t("invoice_form.toast_create_failed")); }
  };

  return (
    <DashboardLayout
      title={t("pages.invoices.title")}
      subtitle={t("pages.invoices.subtitle")}
      actions={
        <button onClick={() => setOpen(true)} data-testid="invoice-new-btn" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-blue">
          <Plus size={14}/> {t("pages.invoices.new_btn")}
        </button>
      }
    >
      {open && (
        <div className="jc-card p-6 mb-6 max-w-2xl">
          <div className="overline mb-3">{t("pages.invoices.new_title")}</div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1">{t("invoice_form.project")}</label>
              <select data-testid="invoice-project-select" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-[var(--jc-border-strong)]">
                <option value="">{t("invoice_form.choose_project")}</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.customer_name} — {p.job_type}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1">{t("invoice_form.type")}</label>
              <select data-testid="invoice-type-select" value={form.invoice_type} onChange={(e) => setForm({ ...form, invoice_type: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-[var(--jc-border-strong)]">
                <option value="deposit">{t("invoice_types.deposit")}</option>
                <option value="progress">{t("invoice_types.progress")}</option>
                <option value="final">{t("invoice_types.final")}</option>
                <option value="change_order">{t("invoice_types.change_order")}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1">{t("invoice_form.amount")}</label>
              <input type="number" data-testid="invoice-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-[var(--jc-border-strong)] font-mono-jc" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1">{t("invoice_form.due_date")}</label>
              <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-[var(--jc-border-strong)]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1">{t("invoice_form.customer_language")}</label>
              <select
                data-testid="invoice-language-select"
                value={form.language}
                onChange={(e) => {
                  setLanguageTouched(true);
                  setForm({ ...form, language: e.target.value });
                }}
                className="w-full px-3 py-2.5 rounded-md border border-[var(--jc-border-strong)]"
              >
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1">{t("invoice_form.notes")}</label>
              <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-[var(--jc-border-strong)]" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-[var(--jc-text-soft)]">{t("invoice_form.cancel")}</button>
            <button onClick={create} data-testid="invoice-create-btn" className="px-4 py-2 rounded-md text-sm font-semibold jc-btn-primary">{t("invoice_form.create")}</button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="jc-card p-12 text-center">
          <div className="font-display text-xl">{t("pages.invoices.empty_title")}</div>
          <p className="text-sm text-[var(--jc-text-soft)] mt-2">{t("pages.invoices.empty_body")}</p>
        </div>
      ) : (
        <div className="jc-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--jc-bg)] text-[var(--jc-text-soft)]">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">{t("invoice_form.columns.customer")}</th>
                <th className="text-left px-5 py-3 font-semibold">{t("invoice_form.columns.type")}</th>
                <th className="text-left px-5 py-3 font-semibold">{t("invoice_form.columns.amount")}</th>
                <th className="text-left px-5 py-3 font-semibold">{t("invoice_form.columns.status")}</th>
                <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">{t("invoice_form.columns.created")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--jc-border)]">
              {items.map((i) => (
                <tr key={i.id} className="hover:bg-[var(--jc-bg)]">
                  <td className="px-5 py-3 font-semibold"><Link to={`/app/invoices/${i.id}`} data-testid={`invoice-row-${i.id}`}>{i.snapshot?.customer?.name || "—"}</Link></td>
                  <td className="px-5 py-3 capitalize">{t(`invoice_types.${i.invoice_type}`)}</td>
                  <td className="px-5 py-3 font-mono-jc">{money(i.amount)}</td>
                  <td className="px-5 py-3"><span className={`text-xs px-2 py-1 rounded-full ${i.payment_status === "paid" ? "bg-green-100 text-green-900" : "bg-[var(--jc-border)]"}`}>{localizePaymentStatus(t, i.payment_status)}</span></td>
                  <td className="px-5 py-3 hidden md:table-cell text-[var(--jc-text-soft)]">{formatDate(i.created_at, lang)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardLayout>
  );
};

export const InvoiceDetail = () => {
  const { t } = useLang();
  const { iid } = useParams();
  const navigate = useNavigate();
  // Phase 5A · Publish current-page workspace hint.
  useLeviWorkspaceHint("invoice_detail", {
    open_document_type: "invoice",
    open_document_id: iid,
    current_section: "invoice_detail",
  });
  const [inv, setInv] = useState(null);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);

  useEffect(() => { api.get(`/invoices/${iid}`).then((r) => setInv(r.data)); }, [iid]);
  if (!inv) return <DashboardLayout title={t("pages.invoices.detail_loading_title")} subtitle={t("pages.invoices.detail_loading_subtitle")}><div /></DashboardLayout>;

  const markPaid = async () => {
    try {
      const { data } = await api.post(`/invoices/${iid}/mark-paid`);
      setInv(data);
      toast.success(t("invoice_detail.toast_marked_paid"));
    } catch { toast.error(t("invoice_detail.toast_mark_paid_failed")); }
  };

  const emailToCustomer = async () => {
    if (sendingRef.current) return;
    const defaultEmail = inv.snapshot?.customer?.email || "";
    const to = window.prompt(t("invoice_detail.prompt_email_to"), defaultEmail);
    if (!to) return;
    const request_key = newRequestKey();
    sendingRef.current = true;
    setSending(true);
    try {
      const { data } = await api.post(`/email/invoice/${iid}`, { to_email: to, request_key });
      const label = data.mock ? t("invoice_detail.toast_email_mock") : t("invoice_detail.toast_email_sent");
      toast.success(`${label} — ${to}`);
    } catch (e) {
      toast.error(localizeSendError(e, t, "invoice_detail.toast_email_failed"));
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const downloadInvoicePdf = async () => {
    try {
      const filename = await downloadPdf(`/invoices/${iid}/pdf`, "invoice.pdf");
      toast.success(t("invoice_detail.toast_downloaded", filename));
    } catch {
      toast.error(t("invoice_detail.toast_pdf_failed"));
    }
  };

  const s = inv.snapshot || {};

  // Slice 2D4 Batch 3 · Document body renders in the invoice's saved
  // language. Contractor-entered content (notes, invoice_terms, customer
  // fields) is displayed as-stored.
  const docLang = inv.language || s.language || "en";
  const dt = makeTranslator(docLang);
  const typeDocKey = `invoice_doc.invoice_type_labels.${inv.invoice_type}`;
  const typeDocVal = dt(typeDocKey);
  const typeDocDisplay = typeDocVal === typeDocKey ? inv.invoice_type.replace("_", " ") : typeDocVal;
  const statusDocKey = `invoice_doc.payment_status_labels.${inv.payment_status}`;
  const statusDocVal = dt(statusDocKey);
  const statusDocDisplay = statusDocVal === statusDocKey ? inv.payment_status : statusDocVal;

  return (
    <DashboardLayout
      title={`${dt("invoice_doc.kind_label")} · ${s.customer?.name || ""}`}
      subtitle={t("invoice_detail.subtitle", typeDocDisplay.toUpperCase(), statusDocDisplay)}
      actions={
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="text-sm text-[var(--jc-text-soft)] inline-flex items-center gap-1"><ArrowLeft size={14}/> {t("invoice_detail.back")}</button>
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)]"><Printer size={14}/> {t("invoice_detail.print")}</button>
          <button onClick={downloadInvoicePdf} data-testid="invoice-download-pdf-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)]"><Download size={14}/> {t("invoice_detail.download_pdf")}</button>
          <button onClick={emailToCustomer} disabled={sending} data-testid="invoice-email-customer-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] disabled:opacity-60 disabled:cursor-not-allowed"><Mail size={14}/> {t("invoice_detail.email_link_to_customer")}</button>
          {inv.payment_status !== "paid" && (
            <button onClick={markPaid} data-testid="invoice-mark-paid-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md jc-btn-primary"><CheckCircle2 size={14}/> {t("invoice_detail.mark_paid")}</button>
          )}
        </div>
      }
    >
      <div className="max-w-3xl mx-auto bg-white border border-[var(--jc-border)] rounded-sm shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-10 print:shadow-none print:border-0">
        <header className="flex items-start justify-between border-b border-[var(--jc-border)] pb-6">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Mark size={32} />
              <div className="font-display text-lg">{s.company?.name || dt("invoice_doc.your_company_fallback")}</div>
            </div>
            <div className="overline">{dt("invoice_doc.kind_label")}</div>
            <div className="font-display text-2xl mt-1">#{inv.id.slice(0, 8).toUpperCase()}</div>
          </div>
          <div className="text-right text-sm">
            <div className="overline mb-1">{dt("invoice_doc.bill_to")}</div>
            <div className="font-semibold">{s.customer?.name}</div>
            <div className="text-[var(--jc-text-soft)]">{s.customer?.email}</div>
            <div className="text-[var(--jc-text-soft)]">{s.customer?.address}</div>
            {inv.language && (
              <div className="mt-1">
                <span data-testid="invoice-lang-badge" className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider bg-[var(--jc-border)] text-[var(--jc-text)]">
                  {inv.language.toUpperCase()}
                </span>
              </div>
            )}
          </div>
        </header>

        <div className="grid md:grid-cols-3 gap-6 mt-6 text-sm">
          <Cell label={dt("invoice_doc.type_label")} value={typeDocDisplay} />
          <Cell label={dt("invoice_doc.issued_label")} value={formatDate(inv.created_at, docLang)} />
          <Cell label={dt("invoice_doc.due_label")} value={inv.due_date ? formatDate(inv.due_date, docLang) : dt("invoice_doc.empty_dash")} />
        </div>

        <section className="mt-8 border-t border-[var(--jc-border)] pt-6">
          <div>
            <div className="overline">{dt("invoice_doc.total_due")}</div>
            <div className="font-display text-4xl mt-1">{money(inv.amount)}</div>
          </div>
        </section>

        {inv.notes && (
          <section className="mt-6 text-sm">
            <div className="overline mb-1">{dt("invoice_doc.notes_heading")}</div>
            <p>{inv.notes}</p>
          </section>
        )}

        <footer className="mt-8 pt-6 border-t border-[var(--jc-border)] text-xs text-[var(--jc-text-soft)]">{s.invoice_terms}</footer>
      </div>
    </DashboardLayout>
  );
};

const Cell = ({ label, value }) => (
  <div>
    <div className="overline mb-1">{label}</div>
    <div className="capitalize">{value}</div>
  </div>
);
